/**
 * Buy/sell/both classification chip + shared option helpers.
 *
 * One place owns the role → {icon, tint, i18n label} mapping so the list chip,
 * the detail inline-edit select, and the quick-create toggle stay in lock-step.
 * The value set, icon name and colour word all come from the generated
 * config-enums (single-sourced from shared/config/product_catalog.config.json):
 * this file only binds the icon NAME → lucide component and the colour WORD →
 * AA-safe tint token, so a config-side icon/colour change flows through without
 * editing this file (mirrors MindMapNode's `workItemTypeIcon` resolution).
 */
import { ArrowLeftRight, ShoppingCart, Tag, type LucideIcon } from 'lucide-react';
import type { TFunction } from 'i18next';

import {
  PRODUCT_ROLE_VALUES,
  productRoleColor,
  productRoleIcon,
  type ProductRole,
} from '../../../../generated/config-enums';
import { Badge } from '../../../../components/ui/badge';
import { cn } from '../../../../lib/utils';

/** Lucide components the product_role config can name. Extend if the config
 * points a role at a new icon (an unknown name falls back to Tag). */
const LUCIDE_BY_NAME: Record<string, LucideIcon> = {
  Tag,
  ShoppingCart,
  ArrowLeftRight,
};

/** AA-safe tint per generated colour word (`bg-<hue>/10 text-<hue>` resolves to
 * the `-strong` foreground — see STYLE_GUIDE § Theme & Colors). */
const TINT_BY_COLOR: Record<string, string> = {
  green: 'bg-success/10 text-success border-success/30',
  blue: 'bg-info/10 text-info border-info/30',
  gray: 'bg-muted text-muted-foreground border-border',
};

const ROLE_LABEL_KEY: Record<ProductRole, string> = {
  sell: 'products.roleSell',
  buy: 'products.roleBuy',
  both: 'products.roleBoth',
};

/** Coerce a loose string (e.g. an inline-edit form value) to a valid role. */
function normalizeRole(role: string): ProductRole {
  return (PRODUCT_ROLE_VALUES as readonly string[]).includes(role) ? (role as ProductRole) : 'both';
}

export function productRoleLabel(t: TFunction, role: ProductRole): string {
  return t(ROLE_LABEL_KEY[role]);
}

/** Options for a Select / ToggleGroup / InlineEditableSelect over every role. */
export function productRoleOptions(t: TFunction): Array<{ value: ProductRole; label: string }> {
  return PRODUCT_ROLE_VALUES.map((value) => ({ value, label: productRoleLabel(t, value) }));
}

interface ProductRoleBadgeProps {
  /** Accepts a loose string (inline-edit form values are untyped); coerced. */
  role: string;
  t: TFunction;
  className?: string;
}

/** Read-only role chip (icon + label) for list rows and detail headers. */
export function ProductRoleBadge({ role, t, className }: ProductRoleBadgeProps) {
  const resolved = normalizeRole(role);
  const Icon = LUCIDE_BY_NAME[productRoleIcon[resolved]] ?? Tag;
  const tint = TINT_BY_COLOR[productRoleColor[resolved]] ?? TINT_BY_COLOR.gray;
  return (
    <Badge variant="outline" className={cn('gap-1 font-medium', tint, className)}>
      <Icon className="h-3 w-3" aria-hidden />
      {productRoleLabel(t, resolved)}
    </Badge>
  );
}
